/**
 * What a backer earns on a booking — one definition, read from their own terms.
 *
 * Before this module the same question had five different answers, and only one
 * of them was right:
 *
 *   1. `flight_data._finance.investor_cost` — stamped on every booking at a flat
 *      14% of the SERVICE fee, regardless of who backs it.
 *   2. `server/cash-ladder.js` — a generic 25% of service fee, with
 *      `RAVI_BACKER_BASE_FEE_RATE = 0.15` defined right beside it and never used.
 *   3. `/api/admin/customer-rankings` — hardcoded `serviceFee * 0.25`.
 *   4. `Analytics.jsx` — branched on a hardcoded EMAIL ADDRESS
 *      (`rtg0795@gmail.com`) to pick 15%-of-base vs 25%-of-service-fee. Correct
 *      today, wrong the moment he changes email, changes rate, or a second
 *      base-fare-share backer exists.
 *   5. `Investor.jsx` — read `app_metadata`, the only one asking the backer.
 *
 * On the eleven bookings assigned to Ravi Teja on 2026-09-01 the spread is not
 * academic: his real earnings are $1,149.60, while the rankings endpoint priced
 * the same trips at $179.65 — understating our cost by $969.95.
 *
 * The terms live on the user's auth `app_metadata`, which is the only place a
 * backer's deal is actually recorded:
 *
 *   backer_earning_model: 'service_fee_share' (default) | 'base_fare_share'
 *   backer_earning_rate:  multiplier on the chosen basis (defaults 0.25 / 0.15)
 *
 * Deliberately pure and dependency-free so the Vite client, the Express server
 * and the one-off scripts can all import it — a shared definition that only one
 * runtime can reach would just become a sixth answer.
 */

/** Investors are flat: 14% of service fee, no per-user terms. */
export const INVESTOR_FEE_RATE_ON_SERVICE_FEE = 0.14;

export const BACKER_MODEL_SERVICE_FEE = 'service_fee_share';
export const BACKER_MODEL_BASE_FARE = 'base_fare_share';

/** Applied when the user carries a model but no explicit rate. */
export const DEFAULT_BACKER_RATE = {
  [BACKER_MODEL_SERVICE_FEE]: 0.25,
  [BACKER_MODEL_BASE_FARE]: 0.15,
};

/**
 * Read a backer's terms. Accepts either an auth user (`{ app_metadata }`) or a
 * bare app_metadata object, because callers hold different shapes: the server
 * has full auth users, the client is handed a trimmed payload.
 *
 * An explicitly stored rate wins even when it is 0 — a 0% backer is unusual but
 * it is a decision someone recorded, and silently substituting 25% would pay
 * them against their own terms. Only a missing or unparseable rate falls back.
 */
export function resolveBackerTerms(source) {
  const md = (source && typeof source === 'object' && source.app_metadata) || source || {};
  const model = md.backer_earning_model === BACKER_MODEL_BASE_FARE
    ? BACKER_MODEL_BASE_FARE
    : BACKER_MODEL_SERVICE_FEE;
  const raw = md.backer_earning_rate;
  const parsed = raw == null ? NaN : parseFloat(raw);
  const rate = Number.isFinite(parsed) ? parsed : DEFAULT_BACKER_RATE[model];
  return { model, rate };
}

/**
 * What the backer earns on one booking — which is exactly what the booking
 * costs Defera in backer fees.
 *
 * `baseFare` and `serviceFee` must both be supplied by the caller from the same
 * derivation it uses everywhere else (`deriveBookingPricing`), so this never
 * becomes a second opinion on what a booking's base fare is.
 */
export function backerEarningsForBooking({ baseFare = 0, serviceFee = 0 }, terms) {
  const t = terms || resolveBackerTerms(null);
  const basis = t.model === BACKER_MODEL_BASE_FARE ? baseFare : serviceFee;
  const n = Number(basis);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n * t.rate;
}

/**
 * id → terms lookup, for the surfaces that price many bookings at once and
 * cannot afford a user fetch per row.
 *
 * Every user is included, not just backers: the map is only ever consulted by
 * `booking.backer_id`, and pre-filtering by role here would mean a booking
 * assigned to someone whose role was later edited silently prices at zero.
 */
export function backerTermsById(users = []) {
  const map = new Map();
  for (const u of users || []) {
    if (u && u.id) map.set(u.id, resolveBackerTerms(u));
  }
  return map;
}

/**
 * Convenience for the common shape: a booking row plus the terms map. Returns 0
 * for unassigned bookings, so callers can sum unconditionally.
 *
 * An assigned booking whose backer is MISSING from the map falls back to the
 * default terms rather than 0 — a cost we cannot look up is still a cost, and
 * under-reporting it is the exact failure this module exists to end.
 */
export function backerCostForBooking(booking, pricing, termsById) {
  const backerId = booking?.backer_id || booking?.backerId;
  if (!backerId) return 0;
  const terms = termsById?.get?.(backerId) || resolveBackerTerms(null);
  return backerEarningsForBooking(pricing, terms);
}
